import { pdf } from '@react-pdf/renderer'
import { Box } from '@mui/material'
import { ReactComponent as DownloadIcon } from 'assets/analytics_download_logo.svg'
import { ReactComponent as ArrowBackIcon } from 'assets/arrow_back_logo.svg'
import { GroupCowStatus, IGroupEntity } from 'models/IGroup'
import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { fetchGroupList } from 'store/groupsSlice'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { COLORS } from 'styles/colors'
import BreadCrumbsGroup from 'ui/breadCrumbs/BreadCrumbsGroup'
import { DAY_METRIC_SECTIONS } from './dayMetrics'
import FeedingAnalyticsPdf, {
	FeedPdfPoint,
	RationPdfLine,
	feedingAnalyticsFileName,
} from './feedingAnalyticsPdf'
import FeedingDateField from './FeedingDateField'
import FeedTrendCard from './FeedTrendCard'
import PsvTrendCard from './PsvTrendCard'
import PushesTrendCard from './PushesTrendCard'
import { useFeedingPlanMetrics } from './useCleaningMetrics'
import MetricCard from './MetricCard'
import RationChangeNotice from './RationChangeNotice'

type StatusFilter = 'milk' | 'dry'

const STATUS_FILTERS: { id: StatusFilter; label: string }[] = [
	{ id: 'milk', label: 'Дойные коровы' },
	{ id: 'dry', label: 'Сухостойные коровы' },
]

const DRY_STATUSES = new Set<GroupCowStatus>([
	GroupCowStatus.Dry,
	GroupCowStatus.Dry_1,
	GroupCowStatus.Dry_2,
])

const isMilkGroup = (group: IGroupEntity) =>
	group.status === GroupCowStatus.Milk

const isDryGroup = (group: IGroupEntity) =>
	group.status != null && DRY_STATUSES.has(group.status)

const isGroupEnabled = (group: IGroupEntity, filters: StatusFilter[]) => {
	if (filters.length === 0) return true
	if (filters.includes('milk') && isMilkGroup(group)) return true
	if (filters.includes('dry') && isDryGroup(group)) return true
	return false
}

const FeedingAnalyticsView = () => {
	const navigate = useNavigate()
	const dispatch = useAppDispatch()
	const { groupList } = useAppSelector(state => state.groupsReducer)
	const [exporting, setExporting] = useState(false)
	const [rationLines, setRationLines] = useState<RationPdfLine[]>([])
	const [feedPdfPoints, setFeedPdfPoints] = useState<FeedPdfPoint[]>([])
	const [statusFilters, setStatusFilters] = useState<StatusFilter[]>([])
	const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([])
	const [dateRange, setDateRange] = useState(() => {
		const yesterday = moment().subtract(1, 'day').startOf('day')
		return { start: yesterday, end: yesterday.clone() }
	})

	useEffect(() => {
		if (groupList.length === 0) {
			dispatch(fetchGroupList())
		}
	}, [dispatch, groupList.length])

	const sortedGroups = useMemo(
		() =>
			groupList
				.slice()
				.sort((a, b) =>
					a.title.localeCompare(b.title, 'ru', { numeric: true }),
				),
		[groupList],
	)

	useEffect(() => {
		setSelectedGroupIds(current =>
			current.filter(id => {
				const group = groupList.find(item => item.id === id)
				return group ? isGroupEnabled(group, statusFilters) : false
			}),
		)
	}, [groupList, statusFilters])

	const toggleStatus = (id: StatusFilter) => {
		setStatusFilters(current =>
			current.includes(id)
				? current.filter(item => item !== id)
				: [...current, id],
		)
	}

	const enabledGroupIds = useMemo(
		() =>
			sortedGroups
				.filter(group => isGroupEnabled(group, statusFilters))
				.map(group => group.id),
		[sortedGroups, statusFilters],
	)
	const metricGroups = useMemo(
		() =>
			sortedGroups.map(group => ({
				id: group.id,
				title: group.title,
				dryNorm: group.food_dry_per_head,
			})),
		[sortedGroups],
	)
	const {
		cleaningCards,
		distributionCards,
		pushesCard,
		nudgeCards,
		psvCard,
		psvTrend,
		pushTrend,
		showTrend,
		hasEnabledData,
		plansReady,
	} = useFeedingPlanMetrics({
		startDate: dateRange.start,
		endDate: dateRange.end,
		selectedGroupIds,
		enabledGroupIds,
		groups: metricGroups,
	})
	const sections = useMemo(
		() =>
			DAY_METRIC_SECTIONS.map(section => {
				if (section.id === 'cleanups') {
					return {
						...section,
						rows: [{ columns: 4 as const, cards: cleaningCards }],
					}
				}
				if (section.id === 'distributions') {
					return {
						...section,
						rows: [{ columns: 4 as const, cards: distributionCards }],
					}
				}
				if (section.id === 'nudges') {
					return {
						...section,
						rows: [
							{
								columns: 4 as const,
								cards: [pushesCard, ...nudgeCards],
							},
						],
					}
				}
				if (section.id === 'psv') {
					return {
						...section,
						rows: [{ columns: 4 as const, cards: [psvCard] }],
					}
				}
				return section
			}),
		[cleaningCards, distributionCards, nudgeCards, psvCard, pushesCard],
	)
	const isPeriod = !dateRange.start.isSame(dateRange.end, 'day')
	const statusNames = [
		statusFilters.includes('milk') ? 'дойные' : null,
		statusFilters.includes('dry') ? 'сухостойные' : null,
	].filter((name): name is string => name != null)
	const emptyNotice =
		!plansReady || hasEnabledData
			? null
			: isPeriod
				? 'Нет данных за весь выбранный период по всем группам'
				: statusNames.length > 0
					? `Нет данных за весь день по выбранному статусу (нет данных по всем группам ${
							statusNames.length > 1 ? 'со статусами' : 'со статусом'
						} "${statusNames.join('" и "')}")`
					: null

	const exportAnalytics = async () => {
		if (emptyNotice || exporting) return
		setExporting(true)
		try {
			const statusLabels = [
				statusFilters.includes('milk') ? 'Дойные коровы' : '',
				statusFilters.includes('dry') ? 'Сухостойные коровы' : '',
			].filter(Boolean)
			const selectedGroups = sortedGroups.filter(group =>
				selectedGroupIds.includes(group.id),
			)
			const subtitle = selectedGroups.length
				? selectedGroups.map(group => group.title).join(', ')
				: statusLabels.join(', ') || 'Все коровы'
			const blob = await pdf(
				<FeedingAnalyticsPdf
					start={dateRange.start}
					end={dateRange.end}
					subtitle={subtitle}
					sections={sections}
					showCharts={showTrend}
					rationLines={rationLines}
					feedPoints={feedPdfPoints}
					pushPoints={pushTrend}
					psvPoints={psvTrend}
				/>,
			).toBlob()
			const url = URL.createObjectURL(blob)
			const link = document.createElement('a')
			link.href = url
			link.download = `${feedingAnalyticsFileName({
				start: dateRange.start,
				end: dateRange.end,
				status: statusLabels.join(', ').toLowerCase(),
				groups: selectedGroups.length
					? selectedGroups.map(group => group.title).join(', ')
					: statusLabels.length
						? ''
						: 'все группы',
			})}.pdf`
			link.click()
			URL.revokeObjectURL(url)
		} finally {
			setExporting(false)
		}
	}

	const toggleGroup = (groupId: number) => {
		const group = groupList.find(item => item.id === groupId)
		if (!group || !isGroupEnabled(group, statusFilters)) return

		setSelectedGroupIds(current =>
			current.includes(groupId)
				? current.filter(id => id !== groupId)
				: [...current, groupId],
		)
	}

	return (
		<Box
			sx={{
				px: '16px',
				pt: '32px',
				pb: '40px',
				boxSizing: 'border-box',
				backgroundColor: COLORS.background,
			}}
		>
			<Box>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						gap: '16px',
					}}
				>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
						<Box
							component='button'
							type='button'
							aria-label='Назад'
							onClick={() => navigate('/feed/analytics')}
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								width: '28px',
								height: '28px',
								mt: '2px',
								p: 0,
								border: 'none',
								background: 'none',
								cursor: 'pointer',
							}}
						>
							<ArrowBackIcon width={24} height={24} />
						</Box>
						<Box
							component='h1'
							sx={{
								m: 0,
								fontSize: '28px',
								fontWeight: 700,
								lineHeight: '34px',
								color: COLORS.black,
							}}
						>
							Аналитика кормления
						</Box>
					</Box>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
						<Box
							component='button'
							type='button'
							disabled={Boolean(emptyNotice) || exporting}
							onClick={exportAnalytics}
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								gap: '8px',
								height: '40px',
								px: '16px',
								borderRadius: '12px',
								border: `1px solid ${emptyNotice ? '#DBDBDB' : COLORS.main}`,
								backgroundColor: 'transparent',
								color: emptyNotice ? '#BDBDC2' : COLORS.main,
								fontFamily: 'Montserrat',
								fontSize: '14px',
								fontWeight: 600,
								lineHeight: '18px',
								cursor: emptyNotice ? 'default' : 'pointer',
								'&:disabled': {
									opacity: 1,
									color: '#BDBDC2',
									borderColor: '#DBDBDB',
								},
								'& path': { stroke: 'currentColor' },
								'&:hover': emptyNotice
									? undefined
									: {
											backgroundColor: COLORS.main,
											color: COLORS.white,
										},
							}}
						>
							<DownloadIcon width={20} height={20} />
							Экспорт
						</Box>
					</Box>
				</Box>

				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: '12px',
						mt: '24px',
					}}
				>
					<BreadCrumbsGroup
						handleActiveFilter={id => toggleStatus(id as StatusFilter)}
						handleRemoveFilter={id =>
							setStatusFilters(current => current.filter(item => item !== id))
						}
						array={STATUS_FILTERS.map(filter => ({
							id: filter.id,
							title: filter.label,
							selected: statusFilters.includes(filter.id),
						}))}
					/>
					<BreadCrumbsGroup
						handleActiveFilter={id => toggleGroup(Number(id))}
						handleRemoveFilter={id =>
							setSelectedGroupIds(current =>
								current.filter(groupId => groupId !== Number(id)),
							)
						}
						array={sortedGroups.map(group => ({
							id: group.id,
							title: group.title,
							selected: selectedGroupIds.includes(group.id),
							disabled: !isGroupEnabled(group, statusFilters),
						}))}
					/>
					<Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
						<FeedingDateField
							startDate={dateRange.start}
							endDate={dateRange.end}
							onChange={(start, end) => setDateRange({ start, end })}
						/>
					</Box>
				</Box>

				{!emptyNotice && (
					<RationChangeNotice
						startDate={dateRange.start}
						endDate={dateRange.end}
						onDays={setRationLines}
					/>
				)}

				{emptyNotice ? (
					<Box
						sx={{
							minHeight: 'calc(100vh - 360px)',
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							justifyContent: 'center',
							gap: '4px',
						}}
					>
						<Box
							sx={{
								fontSize: '14px',
								fontWeight: 700,
								lineHeight: '18px',
								color: COLORS.secondary,
							}}
						>
							Нет данных
						</Box>
						<Box
							sx={{
								fontSize: '14px',
								fontWeight: 400,
								lineHeight: '18px',
								color: COLORS.secondary,
								textAlign: 'center',
							}}
						>
							Нет данных, соответствующих заданным условиям.
						</Box>
					</Box>
				) : !plansReady ? null : (
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: '32px',
							mt: '32px',
						}}
					>
						{sections.map(section => (
							<Box key={section.id}>
								<Box
									component='h2'
									sx={{
										m: 0,
										mb: '20px',
										fontSize: '20px',
										fontWeight: 700,
										lineHeight: '24px',
										color: COLORS.black,
									}}
								>
									{section.title}
								</Box>
								<Box
									sx={{
										display: 'flex',
										flexWrap: 'wrap',
										gap: '16px',
									}}
								>
									{section.rows.flatMap(row =>
										row.cards.map(card => (
											<MetricCard key={card.id} card={card} />
										)),
									)}
								</Box>
								{section.id === 'psv' && showTrend && (
									<Box sx={{ mt: '24px' }}>
										<PsvTrendCard points={psvTrend} />
									</Box>
								)}
								{section.id === 'nudges' && showTrend && (
									<Box sx={{ mt: '24px' }}>
										<PushesTrendCard points={pushTrend} />
									</Box>
								)}
								{section.id === 'distributions' && showTrend && (
									<Box sx={{ mt: '24px' }}>
										<FeedTrendCard
											onData={setFeedPdfPoints}
											startDate={dateRange.start}
											endDate={dateRange.end}
											groupIds={
												selectedGroupIds.length > 0
													? selectedGroupIds
													: enabledGroupIds
											}
											useAllGroups={
												selectedGroupIds.length === 0 &&
												statusFilters.length === 0
											}
										/>
									</Box>
								)}
							</Box>
						))}
					</Box>
				)}
			</Box>
		</Box>
	)
}

export default FeedingAnalyticsView
